import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the DomainRegistrar type
interface DomainRegistrar {
  id: string;
  registrar_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function DomainRegistrarList() {
  const { logout } = useAuth();
  const [registrars, setRegistrars] = useState<DomainRegistrar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadRegistrars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.domainRegistrars.list(PAGE_SIZE, currentPage);
      // setRegistrars(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockRegistrars: DomainRegistrar[] = [
        {
          id: "1",
          registrar_name: "GoDaddy",
          ncii_instances: 678,
          type: "Commercial",
          region: "Global",
          active_urls: 123,
          removed_urls: 555,
          takedown_rate: 0.82,
          avg_response_time: 3.5,
          contacts: ["abuse@godaddy.com", "legal@godaddy.com"],
        },
        {
          id: "2",
          registrar_name: "Namecheap",
          ncii_instances: 534,
          type: "Commercial",
          region: "Global",
          active_urls: 98,
          removed_urls: 436,
          takedown_rate: 0.82,
          avg_response_time: 3.2,
          contacts: ["abuse@namecheap.com", "legal@namecheap.com"],
        },
        {
          id: "3",
          registrar_name: "Google Domains",
          ncii_instances: 412,
          type: "Commercial",
          region: "Global",
          active_urls: 76,
          removed_urls: 336,
          takedown_rate: 0.82,
          avg_response_time: 2.8,
          contacts: ["registrar-abuse@google.com"],
        },
        {
          id: "4",
          registrar_name: "Cloudflare",
          ncii_instances: 389,
          type: "Commercial",
          region: "Global",
          active_urls: 71,
          removed_urls: 318,
          takedown_rate: 0.82,
          avg_response_time: 2.5,
          contacts: ["abuse@cloudflare.com", "trust-safety@cloudflare.com"],
        },
        {
          id: "5",
          registrar_name: "Network Solutions",
          ncii_instances: 456,
          type: "Commercial",
          region: "North America",
          active_urls: 84,
          removed_urls: 372,
          takedown_rate: 0.82,
          avg_response_time: 3.8,
          contacts: ["abuse@networksolutions.com"],
        },
        {
          id: "6",
          registrar_name: "Domain.com",
          ncii_instances: 298,
          type: "Commercial",
          region: "Global",
          active_urls: 54,
          removed_urls: 244,
          takedown_rate: 0.82,
          avg_response_time: 3.3,
          contacts: ["abuse@domain.com", "legal@domain.com"],
        },
        {
          id: "7",
          registrar_name: "Tucows",
          ncii_instances: 367,
          type: "Commercial",
          region: "North America",
          active_urls: 67,
          removed_urls: 300,
          takedown_rate: 0.82,
          avg_response_time: 3.6,
          contacts: ["abuse@tucows.com"],
        },
        {
          id: "8",
          registrar_name: "Dynadot",
          ncii_instances: 234,
          type: "Commercial",
          region: "Global",
          active_urls: 43,
          removed_urls: 191,
          takedown_rate: 0.82,
          avg_response_time: 3.1,
          contacts: ["abuse@dynadot.com", "compliance@dynadot.com"],
        },
        {
          id: "9",
          registrar_name: "Gandi",
          ncii_instances: 189,
          type: "Commercial",
          region: "Europe",
          active_urls: 34,
          removed_urls: 155,
          takedown_rate: 0.82,
          avg_response_time: 2.9,
          contacts: ["abuse@gandi.net"],
        },
        {
          id: "10",
          registrar_name: "1&1 IONOS",
          ncii_instances: 312,
          type: "Commercial",
          region: "Europe",
          active_urls: 57,
          removed_urls: 255,
          takedown_rate: 0.82,
          avg_response_time: 3.4,
          contacts: ["abuse@ionos.com", "legal@ionos.com"],
        },
      ];
      setRegistrars(mockRegistrars);
      setHasMore(false);
      setTotalCount(mockRegistrars.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load domain registrars");
      //   }
      // } else {
      //   setError("Failed to load domain registrars");
      // }
      setError("Failed to load domain registrars");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadRegistrars();
  }, [loadRegistrars]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Domain Registrars table
  const columns: ColumnDef<DomainRegistrar>[] = [
    {
      key: "registrar_name",
      header: "Registrar Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.registrar_name}
        </div>
      ),
      sortValue: (item) => item.registrar_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to registrar details or open modal
            console.log("View domain registrar:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new registrar creation
  const handleNewRegistrar = () => {
    // TODO: Implement new registrar modal or navigation
    console.log("Add new domain registrar");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export domain registrars");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewRegistrar}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Registrar
      </Button>
    </>
  );

  return (
    <DataTable
      title="Domain Registrars"
      subtitle="Track domain registrars and enforcement actions"
      columns={columns}
      data={registrars}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search domain registrars..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="domain registrars"
    />
  );
}



